import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Save, User, Mail, Briefcase, AlertCircle } from 'lucide-react';
import { Aprovador, UpdateAprovadorData } from '../types';
import api from '../services/api';

const AprovadorEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [aprovador, setAprovador] = useState<Aprovador | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UpdateAprovadorData>();

  useEffect(() => {
    const fetchAprovador = async () => {
      try {
        const response = await api.get<Aprovador>(`/aprovadores/${id}`);
        setAprovador(response.data);

        // Preencher formulário com dados existentes
        setValue('nome', response.data.nome);
        setValue('email', response.data.email);
        setValue('cargo', response.data.cargo);
        setValue('ativo', response.data.ativo);
      } catch (error) {
        toast.error('Erro ao carregar dados do aprovador');
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAprovador();
    }
  }, [id, setValue]);

  const onSubmit = async (data: UpdateAprovadorData) => {
    if (!id) return;

    setSaving(true);
    try {
      await api.put(`/aprovadores/${id}`, data);
      toast.success('Aprovador atualizado com sucesso!');
      navigate('/aprovadores');
    } catch (error) {
      toast.error('Erro ao atualizar aprovador');
      console.error('Erro:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  if (!aprovador) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <AlertCircle className='mx-auto h-12 w-12 text-red-500 mb-4' />
          <h2 className='text-xl font-semibold text-gray-900'>Aprovador não encontrado</h2>
          <p className='text-gray-600 mt-2'>O aprovador solicitado não foi encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 py-8'>
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='mb-8'>
          <div className='flex items-center space-x-4'>
            <button
              onClick={() => navigate('/aprovadores')}
              className='flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors'
            >
              <ArrowLeft className='h-5 w-5' />
              <span>Voltar</span>
            </button>
            <div>
              <h1 className='text-3xl font-bold text-gray-900'>Editar Aprovador</h1>
              <p className='text-gray-600 mt-1'>Atualize as informações do aprovador</p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <div className='bg-white rounded-lg shadow'>
          <div className='px-6 py-4 border-b border-gray-200'>
            <h3 className='text-lg font-semibold text-gray-900'>Informações do Aprovador</h3>
          </div>
          <div className='p-6'>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Nome */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Nome *</label>
                  <div className='relative'>
                    <User className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      {...register('nome', { required: 'Nome é obrigatório' })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='Digite o nome completo'
                    />
                  </div>
                  {errors.nome && (
                    <p className='text-red-600 text-sm mt-1'>{errors.nome.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Email *</label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='email'
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido',
                        },
                      })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='exemplo@empresa.com'
                    />
                  </div>
                  {errors.email && (
                    <p className='text-red-600 text-sm mt-1'>{errors.email.message}</p>
                  )}
                </div>

                {/* Cargo */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Cargo *</label>
                  <div className='relative'>
                    <Briefcase className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                    <input
                      type='text'
                      {...register('cargo', { required: 'Cargo é obrigatório' })}
                      className='w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='Ex: Gerente de TI, Coordenador de Projetos'
                    />
                  </div>
                  {errors.cargo && (
                    <p className='text-red-600 text-sm mt-1'>{errors.cargo.message}</p>
                  )}
                </div>

                {/* Status Ativo */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Status</label>
                  <div className='flex items-center space-x-3'>
                    <input
                      type='checkbox'
                      id='ativo'
                      {...register('ativo')}
                      className='h-4 w-4 text-primary-600 focus:ring-primary-500-500 border-gray-300 rounded'
                    />
                    <label htmlFor='ativo' className='text-sm text-gray-700'>
                      Aprovador ativo
                    </label>
                  </div>
                  <p className='text-xs text-gray-500 mt-1'>
                    Aprovadores inativos não podem ser selecionados em novos GMUDs
                  </p>
                </div>
              </div>

              {/* Informações do Sistema */}
              <div className='bg-gray-50 rounded-lg p-4'>
                <h4 className='text-sm font-medium text-gray-900 mb-3'>Informações do Sistema</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500'>ID:</span>
                    <span className='ml-2 text-gray-900'>{aprovador.id}</span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Data de Criação:</span>
                    <span className='ml-2 text-gray-900'>
                      {new Date(aprovador.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Última Atualização:</span>
                    <span className='ml-2 text-gray-900'>
                      {new Date(aprovador.updated_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Status Atual:</span>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                        aprovador.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {aprovador.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className='flex items-center justify-end space-x-3 pt-6 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={() => navigate('/aprovadores')}
                  className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  disabled={saving}
                  className='flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50'
                >
                  <Save className='h-4 w-4' />
                  <span>{saving ? 'Salvando...' : 'Salvar Alterações'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Aviso sobre Desativação */}
        {!aprovador.ativo && (
          <div className='mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg className='h-5 w-5 text-yellow-400' viewBox='0 0 20 20' fill='currentColor'>
                  <path
                    fillRule='evenodd'
                    d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-yellow-800'>Aprovador Inativo</h3>
                <div className='mt-2 text-sm text-yellow-700'>
                  <p>
                    Este aprovador está marcado como inativo. Aprovadores inativos não podem ser
                    selecionados em novos GMUDs, mas continuam visíveis nos GMUDs existentes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AprovadorEdit;
